import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Edit2, Plus, Save, Smile, Trash2, X } from 'react-feather';
import Screen from '../Layouts/Screen/Screen';
import Spinner from '../../global-components/src/components/spinner/spiner';
import { useAuth, useMessage } from '../../global-components/src';
import { errorMessage } from '../../global-components/src/utils/text';
import { api } from '../../services/api';
import './moderator-actions.css';

interface ManagedReaction {
    id: number;
    slug: string;
    emoji: string;
    label: string;
    is_active: boolean;
    position: number;
    in_use: boolean;
}

interface ReactionForm {
    slug: string;
    emoji: string;
    label: string;
    is_active: boolean;
    position: number;
}

const emptyReaction = (position: number): ReactionForm => ({
    slug: '',
    emoji: '',
    label: '',
    is_active: true,
    position,
});

const Reactions = () => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const [reactions, setReactions] = useState<ManagedReaction[]>([]);
    const [form, setForm] = useState<ReactionForm>(emptyReaction(1));
    const [editingId, setEditingId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const loadReactions = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api(token).get<ManagedReaction[]>('/pacoca-tools/reactions');
            setReactions(data);
            setForm(current => current.slug ? current : emptyReaction(data.length + 1));
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [setMessage, token]);

    useEffect(() => {
        void loadReactions();
    }, [loadReactions]);

    const maxPosition = useMemo(
        () => Math.max(1, reactions.length + (editingId ? 0 : 1)),
        [editingId, reactions.length],
    );
    const editingReaction = reactions.find(reaction => reaction.id === editingId);

    const resetForm = () => {
        setEditingId(null);
        setForm(emptyReaction(reactions.length + 1));
    };

    const submitReaction = async (event: FormEvent) => {
        event.preventDefault();
        setSaving(true);

        try {
            const payload = {
                ...form,
                slug: form.slug.trim().toLowerCase(),
                emoji: form.emoji.trim(),
                label: form.label.trim(),
            };

            if (editingId) {
                await api(token).put(`/pacoca-tools/reactions/${editingId}`, payload);
                setMessage('Emoji atualizado.', 'success');
            } else {
                await api(token).post('/pacoca-tools/reactions', payload);
                setMessage('Emoji criado.', 'success');
            }

            resetForm();
            await loadReactions();
            window.dispatchEvent(new Event('pacoca-reactions-updated'));
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    const editReaction = (reaction: ManagedReaction) => {
        setEditingId(reaction.id);
        setForm({
            slug: reaction.slug,
            emoji: reaction.emoji,
            label: reaction.label,
            is_active: reaction.is_active,
            position: reaction.position,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const deleteReaction = async (reaction: ManagedReaction) => {
        if (!window.confirm(`Excluir o emoji ${reaction.emoji} ${reaction.label}?`)) return;

        setDeletingId(reaction.id);
        try {
            await api(token).delete(`/pacoca-tools/reactions/${reaction.id}`);
            setMessage('Emoji excluído.', 'success');
            if (editingId === reaction.id) resetForm();
            await loadReactions();
            window.dispatchEvent(new Event('pacoca-reactions-updated'));
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Screen title="Emojis do Paçoca" paddingTopTitle>
            <Helmet>
                <title>Paçoca - Emojis</title>
            </Helmet>

            <div className="moderator-action-page">
                <section className="moderator-action-card">
                    <div className="moderator-action-heading">
                        <div>
                            <h2><Smile size={21} /> Emojis do Paçoca</h2>
                            <p>Crie, edite, ordene, ative ou desative os emojis exibidos nas reações.</p>
                        </div>
                        <span className="moderator-action-count">{reactions.length}</span>
                    </div>

                    <form className="moderator-reaction-form" onSubmit={submitReaction}>
                        <div className="moderator-emoji-preview" aria-label="Prévia do emoji">
                            {form.emoji || '🙂'}
                        </div>

                        <div className="moderator-action-field">
                            <label htmlFor="reaction-label">Nome</label>
                            <input
                                id="reaction-label"
                                className="form-control"
                                maxLength={60}
                                required
                                value={form.label}
                                onChange={event => setForm(current => ({ ...current, label: event.target.value }))}
                                placeholder="Ex.: Amei"
                            />
                        </div>

                        <div className="moderator-action-field">
                            <label htmlFor="reaction-slug">Identificador</label>
                            <input
                                id="reaction-slug"
                                className="form-control"
                                maxLength={30}
                                pattern="[a-z0-9_]+"
                                required
                                disabled={Boolean(editingReaction?.in_use)}
                                title={editingReaction?.in_use ? 'O identificador não pode mudar depois que o emoji foi usado.' : undefined}
                                value={form.slug}
                                onChange={event => setForm(current => ({
                                    ...current,
                                    slug: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''),
                                }))}
                                placeholder="Ex.: amei"
                            />
                        </div>

                        <div className="moderator-action-field">
                            <label htmlFor="reaction-emoji">Emoji</label>
                            <input
                                id="reaction-emoji"
                                className="form-control moderator-emoji-input"
                                maxLength={10}
                                required
                                value={form.emoji}
                                onChange={event => setForm(current => ({ ...current, emoji: event.target.value }))}
                                placeholder="😍"
                            />
                        </div>

                        <div className="moderator-action-field">
                            <label htmlFor="reaction-position">Posição</label>
                            <select
                                id="reaction-position"
                                className="form-select"
                                value={Math.min(form.position, maxPosition)}
                                onChange={event => setForm(current => ({ ...current, position: Number(event.target.value) }))}
                            >
                                {Array.from({ length: maxPosition }, (_, index) => index + 1).map(position => (
                                    <option key={position} value={position}>{position}</option>
                                ))}
                            </select>
                        </div>

                        <label className="moderator-action-check">
                            <input
                                type="checkbox"
                                checked={form.is_active}
                                onChange={event => setForm(current => ({ ...current, is_active: event.target.checked }))}
                            />
                            Ativo no seletor
                        </label>

                        <div className="moderator-reaction-actions">
                            <button className="btn btn-primary" type="submit" disabled={saving}>
                                {saving ? <Spinner /> : editingId ? <Save size={17} /> : <Plus size={17} />}
                                {editingId ? 'Salvar emoji' : 'Criar emoji'}
                            </button>
                            {editingId && (
                                <button className="btn btn-light" type="button" onClick={resetForm}>
                                    <X size={17} /> Cancelar
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="moderator-reaction-list">
                        {loading ? (
                            <div className="moderator-action-loading"><Spinner /></div>
                        ) : reactions.length === 0 ? (
                            <p className="moderator-action-empty">Nenhum emoji cadastrado.</p>
                        ) : reactions.map(reaction => (
                            <article className={`moderator-reaction-row ${reaction.is_active ? '' : 'is-inactive'}`} key={reaction.id}>
                                <span className="moderator-reaction-position">{reaction.position}</span>
                                <span className="moderator-reaction-emoji">{reaction.emoji}</span>
                                <div className="moderator-reaction-info">
                                    <strong>{reaction.label}</strong>
                                    <small>{reaction.slug} · {reaction.is_active ? 'ativo' : 'inativo'}</small>
                                </div>
                                <button className="btn btn-sm btn-light" type="button" onClick={() => editReaction(reaction)} aria-label={`Editar ${reaction.label}`}>
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    type="button"
                                    disabled={deletingId === reaction.id || reaction.in_use}
                                    title={reaction.in_use ? 'Desative este emoji: ele já foi usado em posts.' : undefined}
                                    onClick={() => void deleteReaction(reaction)}
                                    aria-label={`Excluir ${reaction.label}`}
                                >
                                    {deletingId === reaction.id ? <Spinner /> : <Trash2 size={16} />}
                                </button>
                            </article>
                        ))}
                    </div>
                </section>
            </div>
        </Screen>
    );
};

export default Reactions;
